import React, { useState, useEffect } from 'react';
import { GarageCheckin, UserProfile, Shift } from '../types';
import { supabase } from '../supabaseClient';
import { Car, Clock, CheckCircle2, AlertTriangle, Upload, Loader2, Smartphone, MessageSquare, Camera, Shield, MapPin, X, Star, HelpCircle, ExternalLink } from 'lucide-react';
import { INITIAL_FLEET } from './FleetView';
import { calculateEarnings } from '../services/calculationService';

const normalizePlate = (plate: string) => {
    return (plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
};

const getCarAddress = (plate: string, loadedCars: any[]) => {
    const normalized = normalizePlate(plate);
    if (!normalized) return '';
    
    // 1. Search in database loaded cars
    const dbMatch = loadedCars.find(c => normalizePlate(c.placa) === normalized);
    if (dbMatch) return dbMatch.endereco;

    // 2. Search in INITIAL_FLEET fallback
    const fallbackMatch = INITIAL_FLEET.find(c => normalizePlate(c.placa) === normalized);
    if (fallbackMatch) return fallbackMatch.endereco;

    return '';
};


interface Props {
    checkins: GarageCheckin[];
    userId: string;
    userProfile: UserProfile | null;
    onUpdate: () => void;
}

const colorMap: Record<string, { bg: string; border: string; text: string; icon: string }> = {
    GRAY: { bg: 'bg-slate-800/60', border: 'border-slate-700', text: 'text-slate-400', icon: '⏳' },
    GREEN: { bg: 'bg-emerald-900/30', border: 'border-emerald-500/40', text: 'text-emerald-400', icon: '🟢' },
    YELLOW: { bg: 'bg-amber-900/30', border: 'border-amber-500/40', text: 'text-amber-400', icon: '🟡' },
    RED: { bg: 'bg-red-900/30', border: 'border-red-500/40', text: 'text-red-400', icon: '🔴' },
};

const statusLabels: Record<string, string> = {
    WAITING: 'Aguardando carro na garagem...',
    CAR_READY: '🚗 Carro chegou! Confirme sua presença.',
    DRIVER_OK: 'Registre seus horários de login e finalize o turno.',
    COMPLETED: 'Turno auditado e validado ✅',
};

function calculateLocalColor(checkin: GarageCheckin): string {
    if (!checkin.car_arrived_at || !checkin.login_telegram_at) return checkin.audit_color || 'GRAY';

    const carTime = new Date(checkin.car_arrived_at).getTime();
    const [h, m] = (checkin.scheduled_start || '00:00').split(':').map(Number);
    const schedDate = new Date(checkin.shift_date + 'T00:00:00');
    schedDate.setHours(h, m, 0, 0);
    const schedTime = schedDate.getTime();
    const refTime = Math.max(carTime, schedTime);

    const loginTime = new Date(checkin.login_telegram_at).getTime();
    const diffMin = (loginTime - refTime) / (1000 * 60);

    if (checkin.token_delay && checkin.token_proof_url) {
        return diffMin <= 8 ? 'GREEN' : 'YELLOW';
    }

    if (diffMin <= 5) return 'GREEN';
    if (diffMin <= 8) return 'YELLOW';
    return 'RED';
}

export const GarageDriverCard: React.FC<Props> = ({ checkins, userId, userProfile, onUpdate }) => {
    const [uploadingId, setUploadingId] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [loginTimes, setLoginTimes] = useState<Record<string, { telegram: string; app: string }>>({});
    const [fleetCars, setFleetCars] = useState<any[]>([]);
    
    // States for Checkout Ajuste Real
    const [todayShifts, setTodayShifts] = useState<Shift[]>([]);
    const [fimRealTimes, setFimRealTimes] = useState<Record<string, string>>({});
    const [descansoSaved, setDescansoSaved] = useState<Record<string, string>>({});
    const [logoutTelegramTimes, setLogoutTelegramTimes] = useState<Record<string, string>>({});
    const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({});

    const toggleDetails = (id: string) => {
        setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Tour states
    const [garageTourStep, setGarageTourStep] = useState(0);
    const [hasSeenGarageTour, setHasSeenGarageTour] = useState(() => localStorage.getItem('driver_score_garage_tour_completed') === 'true');

    useEffect(() => {
        const fetchCars = async () => {
            try {
                const { data } = await supabase.from('fleet_cars').select('placa, endereco');
                if (data) setFleetCars(data);
            } catch (err) {
                console.error("Error loading fleet cars for driver card:", err);
            }
        };
        fetchCars();
    }, []);

    // Fetch and sync shifts for today to auto-fill Checkout details
    useEffect(() => {
        const fetchTodayShifts = async () => {
            if (!userId) return;
            const today = new Date().toISOString().split('T')[0];
            try {
                const { data } = await supabase
                    .from('user_shifts')
                    .select('*')
                    .eq('user_id', userId)
                    .eq('date', today);
                if (data) {
                    const mapped = data.map(s => ({
                        id: s.id,
                        user_id: s.user_id,
                        type: s.type || 'WORK',
                        noShow: s.no_show || false,
                        date: s.date,
                        startTime: s.start_time || '06:00',
                        endTime: s.end_time || '16:00',
                        isHoliday: s.is_holiday || false,
                        bilingualStart: s.bilingual_start,
                        bilingualEnd: s.bilingual_end,
                        totalEarnings: s.total_earnings || 0,
                        totalHours: s.total_hours || 0,
                        tips: s.tips || 0,
                        appTips: 0,
                        breakdown: s.breakdown || { basePay: 0, nightPay: 0, bilingualPay: 0 },
                        expenses: s.expenses || [],
                        extraEarnings: s.extra_earnings || [],
                        unpaidBreakMinutes: s.unpaid_break_minutes || 0,
                        plannedStartTime: s.planned_start_time,
                        plannedEndTime: s.planned_end_time,
                        vehiclePlate: s.vehicle_plate,
                        carReadyOnTime: s.car_ready_on_time,
                        carArrivalTime: s.car_arrival_time,
                        loginAppTime: s.login_app_time,
                        loginTelegramTime: s.login_telegram_time
                    } as Shift));

                    setTodayShifts(mapped);

                    // Initialize end times and break minutes
                    const endMap: Record<string, string> = {};
                    const breakMap: Record<string, string> = {};
                    const logoutTelegramMap: Record<string, string> = {};
                    
                    checkins.forEach(c => {
                        const match = mapped.find(s => {
                            const sched = s.plannedStartTime || s.startTime;
                            return sched === c.scheduled_start && s.type === 'WORK';
                        });
                        if (match) {
                            endMap[c.id] = match.endTime || match.plannedEndTime || "16:00";
                            breakMap[c.id] = (match.unpaidBreakMinutes || 0).toString();
                            logoutTelegramMap[c.id] = match.logoutTelegramTime || match.endTime || match.plannedEndTime || "16:00";
                        } else {
                            endMap[c.id] = "16:00";
                            breakMap[c.id] = "0";
                            logoutTelegramMap[c.id] = "16:00";
                        }
                    });
                    setFimRealTimes(endMap);
                    setDescansoSaved(breakMap);
                    setLogoutTelegramTimes(logoutTelegramMap);
                }
            } catch (err) {
                console.error("Error loading today shifts:", err);
            }
        };
        fetchTodayShifts();
    }, [checkins, userId]);

    // Initialize login times from existing data
    useEffect(() => {
        const times: Record<string, { telegram: string; app: string }> = {};
        checkins.forEach(c => {
            const tgTime = c.login_telegram_at ? new Date(c.login_telegram_at).toTimeString().slice(0, 5) : '';
            const appTime = c.login_app_at ? new Date(c.login_app_at).toTimeString().slice(0, 5) : '';
            times[c.id] = { telegram: tgTime, app: appTime };
        });
        setLoginTimes(times);
    }, [checkins]);

    // Realtime subscription
    useEffect(() => {
        const channel = supabase
            .channel('driver-garage')
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'garage_checkins',
                filter: `user_id=eq.${userId}`
            }, () => {
                onUpdate();
            })
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [userId, onUpdate]);

    // Auto-trigger tour if enabled and not seen yet
    useEffect(() => {
        const isTourEnabled = localStorage.getItem('driver_score_garage_tour_enabled') !== 'false';
        if (!hasSeenGarageTour && isTourEnabled && checkins.length > 0) {
            setGarageTourStep(1);
        }
    }, [checkins, hasSeenGarageTour]);

    const finishGarageTour = () => {
        setGarageTourStep(0);
        setHasSeenGarageTour(true);
        localStorage.setItem('driver_score_garage_tour_completed', 'true');
    };

    const handleCarArrived = async (checkin: GarageCheckin) => {
        setSavingId(checkin.id);
        try {
            const now = new Date();
            const arrivedAtIso = now.toISOString();
            const arrivalTimeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

            // 1. Update garage_checkins
            const { error: checkinErr } = await supabase
                .from('garage_checkins')
                .update({
                    car_arrived_at: arrivedAtIso,
                    status: 'CAR_READY',
                    updated_at: arrivedAtIso,
                })
                .eq('id', checkin.id)
                .eq('user_id', userId);

            if (checkinErr) throw checkinErr;

            // 2. Find and update corresponding user_shifts
            const { data: matchedShifts, error: queryErr } = await supabase
                .from('user_shifts')
                .select('*')
                .eq('user_id', userId)
                .eq('date', checkin.shift_date);

            if (queryErr) {
                console.error("Error querying user shifts:", queryErr);
            } else if (matchedShifts && matchedShifts.length > 0) {
                const targetShift = matchedShifts.find(s => {
                    const sched = s.planned_start_time || s.start_time;
                    return sched === checkin.scheduled_start && s.type === 'WORK';
                });

                if (targetShift) {
                    const { error: shiftUpdateErr } = await supabase
                        .from('user_shifts')
                        .update({
                            car_arrival_time: arrivalTimeStr,
                        })
                        .eq('id', targetShift.id);

                    if (shiftUpdateErr) {
                        console.error("Error updating user shift car arrival time:", shiftUpdateErr);
                    }
                }
            }

            onUpdate();
        } catch (err: any) {
            alert('Erro: ' + err.message);
        } finally {
            setSavingId(null);
        }
    };

    const handleDriverConfirm = async (checkinId: string) => {
        setSavingId(checkinId);
        try {
            const { error } = await supabase
                .from('garage_checkins')
                .update({
                    driver_confirmed_at: new Date().toISOString(),
                    status: 'DRIVER_OK',
                    updated_at: new Date().toISOString(),
                })
                .eq('id', checkinId)
                .eq('user_id', userId);
            if (error) throw error;
            onUpdate();
        } catch (err: any) {
            alert('Erro: ' + err.message);
        } finally {
            setSavingId(null);
        }
    };

    const handleSaveLoginTimes = async (checkin: GarageCheckin) => {
        const times = loginTimes[checkin.id];
        if (!times) return;

        setSavingId(checkin.id);
        try {
            const shiftDate = checkin.shift_date;
            const updates: any = { updated_at: new Date().toISOString() };

            if (times.telegram) {
                updates.login_telegram_at = new Date(`${shiftDate}T${times.telegram}:00`).toISOString();
            }
            if (times.app) {
                updates.login_app_at = new Date(`${shiftDate}T${times.app}:00`).toISOString();
            }

            // 1. Update garage_checkins
            const { error: checkinErr } = await supabase
                .from('garage_checkins')
                .update(updates)
                .eq('id', checkin.id)
                .eq('user_id', userId);
            if (checkinErr) throw checkinErr;

            // 2. Sync to user_shifts
            const { data: matchedShifts, error: queryErr } = await supabase
                .from('user_shifts')
                .select('*')
                .eq('user_id', userId)
                .eq('date', checkin.shift_date);

            if (queryErr) {
                console.error("Error querying shifts:", queryErr);
            } else if (matchedShifts && matchedShifts.length > 0) {
                const targetShift = matchedShifts.find(s => {
                    const sched = s.planned_start_time || s.start_time;
                    return sched === checkin.scheduled_start && s.type === 'WORK';
                });

                if (targetShift) {
                    const { error: shiftUpdateErr } = await supabase
                        .from('user_shifts')
                        .update({
                            login_telegram_time: times.telegram || null,
                            login_app_time: times.app || null
                        })
                        .eq('id', targetShift.id);

                    if (shiftUpdateErr) {
                        console.error("Error syncing shift login times:", shiftUpdateErr);
                    }
                }
            }

            onUpdate();
            alert('✅ Horários de login salvos e sincronizados com o histórico!');
        } catch (err: any) {
            alert('Erro ao salvar horários: ' + err.message);
        } finally {
            setSavingId(null);
        }
    };

    const handleFinalizeShift = async (checkin: GarageCheckin, fimReal: string, descansoSalvo: number, logoutTelegram: string) => {
        if (!fimReal) return;
        
        const confirmed = window.confirm("Deseja confirmar a finalização deste turno? Esta ação calculará sua auditoria centesimal.");
        if (!confirmed) return;

        setSavingId(checkin.id);
        try {
            const nowIso = new Date().toISOString();

            // 1. Update garage_checkins status & logout timestamp
            const updates: any = {
                status: 'COMPLETED',
                updated_at: nowIso
            };
            if (logoutTelegram) {
                updates.logout_telegram_at = new Date(`${checkin.shift_date}T${logoutTelegram}:00`).toISOString();
            }

            let checkinResponse = await supabase
                .from('garage_checkins')
                .update(updates)
                .eq('id', checkin.id)
                .eq('user_id', userId);

            if (checkinResponse.error && (checkinResponse.error.code === 'PGRST204' || checkinResponse.error.message?.includes('logout_telegram_at'))) {
                console.warn("DB migration for logout_telegram_at not applied yet. Retrying without it...");
                const cleanedUpdates = { ...updates };
                delete (cleanedUpdates as any).logout_telegram_at;
                checkinResponse = await supabase
                    .from('garage_checkins')
                    .update(cleanedUpdates)
                    .eq('id', checkin.id)
                    .eq('user_id', userId);
            }

            const checkinErr = checkinResponse.error;

            if (checkinErr) throw checkinErr;

            // 2. Sync and Recalculate corresponding user_shifts
            const { data: matchedShifts, error: queryErr } = await supabase
                .from('user_shifts')
                .select('*')
                .eq('user_id', userId)
                .eq('date', checkin.shift_date);

            if (queryErr) throw queryErr;

            if (matchedShifts && matchedShifts.length > 0) {
                const targetShift = matchedShifts.find(s => {
                    const sched = s.planned_start_time || s.start_time;
                    return sched === checkin.scheduled_start && s.type === 'WORK';
                });

                if (targetShift) {
                    // Start time is real start time or falls back to planned start time
                    const realStart = targetShift.login_app_time || targetShift.login_telegram_time || targetShift.start_time || checkin.scheduled_start;

                    // Robust mathematical calculations for earnings
                    const result = calculateEarnings(
                        targetShift.date,
                        realStart,
                        fimReal,
                        targetShift.is_holiday || false,
                        targetShift.bilingual_start,
                        targetShift.bilingual_end,
                        userProfile?.generation || 'OLD'
                    );

                    // Rebuild updated db shift structure (snake_case database representation)
                    const updatedDbShift = {
                        ...targetShift,
                        start_time: realStart,
                        end_time: fimReal,
                        unpaid_break_minutes: descansoSalvo,
                        total_earnings: result.totalEarnings,
                        total_hours: result.totalHours,
                        breakdown: result.breakdown,
                        car_arrival_time: targetShift.car_arrival_time || null,
                        login_app_time: targetShift.login_app_time || null,
                        login_telegram_time: targetShift.login_telegram_time || null,
                        logout_telegram_time: logoutTelegram || null
                    };

                    let shiftResponse = await supabase
                        .from('user_shifts')
                        .upsert(updatedDbShift);

                    if (shiftResponse.error && (shiftResponse.error.code === 'PGRST204' || shiftResponse.error.message?.includes('logout_telegram_time'))) {
                        console.warn("DB migration for logout_telegram_time not applied yet. Retrying without it...");
                        const cleanedDbShift = { ...updatedDbShift };
                        delete (cleanedDbShift as any).logout_telegram_time;
                        shiftResponse = await supabase
                            .from('user_shifts')
                            .upsert(cleanedDbShift);
                    }

                    const shiftUpdateErr = shiftResponse.error;

                    if (shiftUpdateErr) throw shiftUpdateErr;
                }
            }

            onUpdate();
            alert('✅ Turno concluído e auditoria de check-out finalizada!');
        } catch (err: any) {
            alert('Erro ao finalizar turno: ' + err.message);
        } finally {
            setSavingId(null);
        }
    };

    const handleTokenDelayUpload = async (checkinId: string, file: File) => {
        setUploadingId(checkinId);
        try {
            const ext = file.name.split('.').pop();
            const path = `token-proofs/${userId}/${checkinId}.${ext}`;

            const { error: uploadErr } = await supabase.storage
                .from('garage-proofs')
                .upload(path, file, { upsert: true });

            if (uploadErr) {
                console.warn('Storage upload failed, using inline note:', uploadErr.message);
                await supabase
                    .from('garage_checkins')
                    .update({
                        token_delay: true,
                        token_proof_url: `proof_uploaded_${new Date().toISOString()}`,
                        exception_note: 'Token do WhatsApp demorou. Print anexado pelo motorista.',
                        updated_at: new Date().toISOString(),
                    })
                    .eq('id', checkinId)
                    .eq('user_id', userId);
            } else {
                const { data: urlData } = supabase.storage
                    .from('garage-proofs')
                    .getPublicUrl(path);

                await supabase
                    .from('garage_checkins')
                    .update({
                        token_delay: true,
                        token_proof_url: urlData.publicUrl,
                        exception_note: 'Token do WhatsApp demorou. Print anexado pelo motorista.',
                        updated_at: new Date().toISOString(),
                    })
                    .eq('id', checkinId)
                    .eq('user_id', userId);
            }

            onUpdate();
            alert('✅ Comprovante de delay do token enviado com sucesso!');
        } catch (err: any) {
            alert('Erro ao enviar print: ' + err.message);
        } finally {
            setUploadingId(null);
        }
    };

    const GarageTourOverlay = () => {
        if (garageTourStep === 0) return null;

        const tourSteps = [
            {
                title: "Ajuste Real na Garagem! ⚙️",
                text: "Aqui você controla as ações do seu turno em tempo real, garantindo auditoria centesimal exata do seu descanso e isenção de multas de logística!",
            },
            {
                title: "Chegada do Carro 🚗",
                text: "Assim que chegar na garagem e localizar seu veículo, clique no botão 'O Carro Chegou!'. Isso envia um aviso em tempo real para o agente da frota saber que você está pronto.",
            },
            {
                title: "Presença e Sincronização 🟢",
                text: "Após o carro ser liberado pelo agente, confirme sua presença clicando em 'Estou no Carro!'.",
            },
            {
                title: "Logins de Serviço 📲",
                text: "Insira os horários exatos em que realizou os logins no Telegram e no Rhino App. Salvar esses horários atualiza seus dados da Garagem e do Histórico simultaneamente!",
            },
            {
                title: "Fim do Turno & Deputy Check-out 🏁",
                text: "Ao finalizar o dia de trabalho, informe o horário de término real, os minutos de descanso salvos e faça o check-out oficial no Deputy. Ao clicar em 'Finalizar Turno', o Driver Score calcula tudo automaticamente no seu extrato!",
            }
        ];

        const current = tourSteps[garageTourStep - 1];

        return (
            <div className="fixed inset-0 z-[200] flex items-center justify-center animate-fade-in bg-black/75 backdrop-blur-xs">
                <div className="bg-slate-800 border border-orange-500/50 p-6 rounded-2xl shadow-2xl max-w-sm w-full mx-4 relative animate-scale-in">
                    <div className="flex justify-between items-start mb-4">
                        <div className="bg-orange-600 p-3 rounded-full shadow-lg shadow-orange-900/40 -mt-10 mb-2 border-4 border-slate-900">
                            <Star className="w-8 h-8 text-white" />
                        </div>
                        <button onClick={finishGarageTour} className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <h3 className="text-lg font-bold text-white mb-2">{current.title}</h3>
                    <p className="text-slate-300 text-xs leading-relaxed mb-6">{current.text}</p>

                    <div className="flex items-center justify-between">
                        <div className="flex gap-1.5">
                            {[1, 2, 3, 4, 5].map(s => (
                                <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === garageTourStep ? 'w-5 bg-orange-500' : 'w-1.5 bg-slate-600'}`} />
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={finishGarageTour}
                                className="text-slate-500 hover:text-slate-400 text-[10px] underline cursor-pointer"
                            >
                                Pular
                            </button>
                            <button
                                onClick={() => {
                                    if (garageTourStep < 5) {
                                        setGarageTourStep(prev => prev + 1);
                                    } else {
                                        finishGarageTour();
                                    }
                                }}
                                className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-colors shadow-lg active:scale-95"
                            >
                                {garageTourStep === 5 ? 'Entendi!' : 'Próximo'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (checkins.length === 0) return null;

    return (
        <div className="space-y-3 mb-4 animate-fade-in">
            <GarageTourOverlay />

            {/* Header Dashboard do Ajuste Real */}
            <div className="flex items-center justify-between bg-slate-900/50 p-4 rounded-2xl border border-slate-700">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-indigo-600/10 rounded-lg">
                        <Smartphone className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                        <h3 className="text-white font-black text-sm">Painel de Ajuste Real</h3>
                        <p className="text-[10px] text-slate-400">Controle e auditoria centesimal de turnos</p>
                    </div>
                </div>
                <button
                    onClick={() => setGarageTourStep(1)}
                    className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-orange-400 border border-slate-700 rounded-xl px-3 py-2 text-xs font-bold transition-all active:scale-95"
                >
                    <HelpCircle className="w-4 h-4" />
                    Como funciona?
                </button>
            </div>

            {/* Aviso de registro antecipado */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-[10px] text-amber-300/80 leading-relaxed">
                    <strong className="text-amber-400">Lembrete:</strong> Registre seus turnos com antecedência! 
                    Ouro = segunda-feira, Prata/Bronze = terça-feira, ou pelo menos <strong>30 min antes</strong> do turno.
                    Não esqueça — turnos não registrados não aparecem para o agente da garagem.
                </p>
            </div>

            {checkins.map((checkin) => {
                const color = calculateLocalColor(checkin);
                const style = colorMap[color] || colorMap.GRAY;
                const times = loginTimes[checkin.id] || { telegram: '', app: '' };
                const isCompleted = checkin.status === 'COMPLETED';
                const isSaving = savingId === checkin.id;
                const isUploading = uploadingId === checkin.id;

                return (
                    <div key={checkin.id} className={`${style.bg} border ${style.border} rounded-2xl p-4 transition-all shadow-lg`}>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Car className={`w-5 h-5 ${style.text}`} />
                                <div>
                                    <h4 className="text-white font-bold text-sm">
                                        Turno às {checkin.scheduled_start}
                                    </h4>
                                    <p className="text-slate-400 text-[10px]">
                                        Placa: <strong className="text-slate-300">{checkin.vehicle_plate}</strong>
                                        {checkin.car_swapped && (
                                            <span className="text-amber-400 ml-2">(Carro trocado)</span>
                                        )}
                                    </p>
                                    {getCarAddress(checkin.vehicle_plate, fleetCars) && (
                                        <p className="text-[10px] text-slate-500 font-medium flex items-center gap-1 mt-0.5">
                                            <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                                            {getCarAddress(checkin.vehicle_plate, fleetCars)}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <span className="text-lg">{style.icon}</span>
                        </div>

                        {/* Status */}
                        <p className={`text-xs font-medium mb-3 ${style.text}`}>
                            {statusLabels[checkin.status] || checkin.status}
                        </p>

                        {/* Botão Chegar Carro */}
                        {checkin.status === 'WAITING' && (
                            <button
                                onClick={() => handleCarArrived(checkin)}
                                disabled={isSaving}
                                className="group relative overflow-hidden w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 px-4 rounded-xl mb-3 transition-all disabled:opacity-50 active:scale-[0.98] shadow-lg shadow-blue-900/20"
                            >
                                {isSaving ? (
                                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                                ) : (
                                    <>
                                        <span className="mr-8 transition-opacity duration-500 group-hover:opacity-0 flex items-center justify-center gap-2">
                                            <Car className="w-5 h-5" />
                                            O Carro Chegou!
                                        </span>
                                        <i className="absolute right-1 top-1 bottom-1 rounded-lg z-10 grid w-1/4 place-items-center transition-all duration-500 bg-white/10 group-hover:w-[calc(100%-0.5rem)] group-active:scale-95 text-white">
                                            <div className="flex items-center gap-2">
                                                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 whitespace-nowrap text-sm">Registrar Chegada</span>
                                                <Car className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                                            </div>
                                        </i>
                                    </>
                                )}
                            </button>
                        )}

                        {/* Botão Confirmar Presença */}
                        {checkin.status === 'CAR_READY' && !checkin.driver_confirmed_at && (
                            <button
                                onClick={() => handleDriverConfirm(checkin.id)}
                                disabled={isSaving}
                                className="w-full bg-emerald-600 hover:bg-emerald-500 active:scale-[0.98] text-white font-bold py-3 px-4 rounded-xl mb-3 flex items-center justify-center gap-2 transition-all disabled:opacity-50 shadow-md"
                            >
                                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                    <>
                                        <CheckCircle2 className="w-5 h-5" />
                                        Estou no Carro!
                                    </>
                                )}
                            </button>
                        )}

                        {/* Campos de Login */}
                        {(checkin.status === 'DRIVER_OK' || checkin.status === 'CAR_READY') && checkin.driver_confirmed_at && !isCompleted && (
                            <div className="space-y-3 mt-2">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 flex items-center gap-1">
                                            <MessageSquare className="w-3 h-3" /> Login Telegram
                                        </label>
                                        <input
                                            type="time"
                                            value={times.telegram}
                                            onChange={e => setLoginTimes(prev => ({
                                                ...prev,
                                                [checkin.id]: { ...prev[checkin.id], telegram: e.target.value }
                                            }))}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none font-mono"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 flex items-center gap-1">
                                            <Smartphone className="w-3 h-3" /> Login App
                                        </label>
                                        <input
                                            type="time"
                                            value={times.app}
                                            onChange={e => setLoginTimes(prev => ({
                                                ...prev,
                                                [checkin.id]: { ...prev[checkin.id], app: e.target.value }
                                            }))}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none font-mono"
                                        />
                                    </div>
                                </div>
                                
                                {/* Dica WhatsApp */}
                                <p className="text-[9px] text-slate-400/80 italic text-center bg-slate-950/20 py-1.5 px-2 rounded-lg border border-slate-800">
                                    💡 <strong>Dica de Ouro:</strong> Você encontra esses horários exatos de login na mensagem que chega no seu <strong>WhatsApp</strong> ao se logar.
                                </p>

                                <button
                                    onClick={() => handleSaveLoginTimes(checkin)}
                                    disabled={isSaving || (!times.telegram && !times.app)}
                                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 text-sm shadow-md"
                                >
                                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                        <>
                                            <Clock className="w-4 h-4" />
                                            Salvar Horários de Login
                                        </>
                                    )}
                                </button>

                                {/* Token delay proof */}
                                <div className="flex items-center gap-2 pt-1 border-b border-slate-800 pb-3">
                                    <label className="flex items-center gap-1.5 cursor-pointer text-[10px] text-slate-500 hover:text-amber-400 transition-colors font-medium">
                                        <Camera className="w-3.5 h-3.5" />
                                        Token do WhatsApp demorou? Envie print como prova
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={e => {
                                                const file = e.target.files?.[0];
                                                if (file) handleTokenDelayUpload(checkin.id, file);
                                            }}
                                        />
                                    </label>
                                    {isUploading && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
                                    {checkin.token_delay && (
                                        <span className="text-[10px] text-emerald-400 font-bold">✓ Prova enviada</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Bloco de Finalização do Turno (Check-out) */}
                        {checkin.status === 'DRIVER_OK' && !isCompleted && (
                            <div className="mt-4 pt-4 border-t border-slate-700 space-y-4 animate-in slide-in-from-bottom-2 duration-300">
                                <h5 className="text-white font-bold text-xs uppercase tracking-widest flex items-center gap-1.5">
                                    🏁 Finalização do Turno (Ajuste Real)
                                </h5>
                                
                                <div className="grid grid-cols-3 gap-2.5">
                                    <div>
                                        <label className="text-[9px] text-slate-400 block mb-1 font-semibold truncate">Fim Real (Deputy)</label>
                                        <input 
                                            type="time"
                                            value={fimRealTimes[checkin.id] || "16:00"}
                                            onChange={(e) => setFimRealTimes(prev => ({ ...prev, [checkin.id]: e.target.value }))}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white font-mono text-xs focus:border-orange-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] text-slate-400 block mb-1 font-semibold truncate">Fim Telegram</label>
                                        <input 
                                            type="time"
                                            value={logoutTelegramTimes[checkin.id] || "16:00"}
                                            onChange={(e) => setLogoutTelegramTimes(prev => ({ ...prev, [checkin.id]: e.target.value }))}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white font-mono text-xs focus:border-orange-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] text-slate-400 block mb-1 font-semibold truncate">Descanso (Min)</label>
                                        <input 
                                            type="number"
                                            value={descansoSaved[checkin.id] || "0"}
                                            onChange={(e) => setDescansoSaved(prev => ({ ...prev, [checkin.id]: e.target.value }))}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white font-mono text-xs focus:border-orange-500 outline-none"
                                            placeholder="Minutos..."
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <button 
                                        onClick={() => handleFinalizeShift(checkin, fimRealTimes[checkin.id] || "16:00", parseInt(descansoSaved[checkin.id] || "0"), logoutTelegramTimes[checkin.id] || "")}
                                        disabled={isSaving}
                                        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-3.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 transition-all active:scale-95"
                                    >
                                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin animate-spin-slow" /> : <CheckCircle2 className="w-4 h-4" />}
                                        Finalizar e Auditorizar Turno 🏁
                                    </button>
                                    
                                    <p className="text-[9px] text-slate-500 leading-relaxed text-center mt-2 bg-slate-950/20 py-2 px-3 rounded-lg border border-slate-800/40">
                                        💡 <strong>Aviso:</strong> Caso queira ajustar alguma informação após salvar, basta acessar a aba <strong>Histórico</strong> para editar o turno a qualquer momento.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Completed Summary */}
                        {isCompleted && (
                            <div className="mt-2 bg-slate-900/50 rounded-xl p-3.5 border border-slate-800 space-y-2 animate-in fade-in">
                                <div className="flex items-center gap-2 text-emerald-400 text-xs font-black uppercase tracking-wider">
                                    <Shield className="w-4 h-4" />
                                    Turno Auditado
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-400">
                                    {checkin.car_arrived_at && (
                                        <span>🚗 Carro: {new Date(checkin.car_arrived_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                    )}
                                    {checkin.login_telegram_at && (
                                        <span>📲 Telegram: {new Date(checkin.login_telegram_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                    )}
                                </div>
                                {todayShifts.find(s => {
                                    const sched = s.plannedStartTime || s.startTime;
                                    return sched === checkin.scheduled_start && s.type === 'WORK';
                                }) && (
                                    <div className="text-[10px] text-emerald-500/80 bg-emerald-950/20 border border-emerald-900/20 rounded-lg p-2 mt-1">
                                        ⏱️ Horários Reais: {
                                            (() => {
                                                const s = todayShifts.find(s => (s.plannedStartTime || s.startTime) === checkin.scheduled_start && s.type === 'WORK');
                                                return `${s?.startTime} às ${s?.endTime} (${s?.unpaidBreakMinutes}min descanso)`;
                                            })()
                                        }
                                    </div>
                                )}

                                {/* Expandable Details Panel */}
                                <button
                                    onClick={() => toggleDetails(checkin.id)}
                                    className="w-full flex items-center justify-between text-[11px] font-bold text-indigo-400 hover:text-indigo-300 bg-indigo-950/20 border border-indigo-900/30 rounded-lg py-2 px-3 transition-colors mt-2"
                                >
                                    <span className="flex items-center gap-1.5">📋 Ver Detalhes do Turno</span>
                                    <span>{expandedDetails[checkin.id] ? '▲ Ocultar' : '▼ Expandir'}</span>
                                </button>

                                {expandedDetails[checkin.id] && (
                                    <div className="mt-2 p-3 bg-slate-900/90 border border-slate-700 rounded-xl space-y-2.5 animate-in slide-in-from-top-1 duration-200">
                                        <div className="text-[10px] text-slate-300 font-bold border-b border-slate-800 pb-1 flex justify-between items-center">
                                            <span>Métricas Finais Auditadas</span>
                                            <span className="text-indigo-400">Logística de Auditoria</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-400">
                                            <div className="flex flex-col p-1.5 bg-slate-950/40 rounded border border-slate-800/60">
                                                <span className="text-slate-500 font-semibold">1. Horário Programado:</span>
                                                <span className="text-slate-300 font-mono font-bold mt-0.5">{checkin.scheduled_start}</span>
                                            </div>
                                            <div className="flex flex-col p-1.5 bg-slate-950/40 rounded border border-slate-800/60">
                                                <span className="text-slate-500 font-semibold">2. Chegada do Carro:</span>
                                                <span className="text-slate-300 font-mono font-bold mt-0.5">
                                                    {checkin.car_arrived_at 
                                                        ? new Date(checkin.car_arrived_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) 
                                                        : '--:--'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col p-1.5 bg-slate-950/40 rounded border border-slate-800/60">
                                                <span className="text-slate-500 font-semibold">3. Login Telegram:</span>
                                                <span className="text-slate-300 font-mono font-bold mt-0.5">
                                                    {checkin.login_telegram_at 
                                                        ? new Date(checkin.login_telegram_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) 
                                                        : '--:--'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col p-1.5 bg-slate-950/40 rounded border border-slate-800/60">
                                                <span className="text-slate-500 font-semibold">4. Logout Telegram:</span>
                                                <span className="text-amber-400 font-mono font-bold mt-0.5">
                                                    {checkin.logout_telegram_at 
                                                        ? new Date(checkin.logout_telegram_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) 
                                                        : (() => {
                                                            const s = todayShifts.find(s => (s.plannedStartTime || s.startTime) === checkin.scheduled_start && s.type === 'WORK');
                                                            return s?.logoutTelegramTime || '--:--';
                                                        })()}
                                                </span>
                                            </div>
                                            <div className="flex flex-col p-1.5 bg-slate-950/40 rounded border border-slate-800/60">
                                                <span className="text-slate-500 font-semibold">5. Login Driver App:</span>
                                                <span className="text-slate-300 font-mono font-bold mt-0.5">
                                                    {checkin.login_app_at 
                                                        ? new Date(checkin.login_app_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) 
                                                        : '--:--'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col p-1.5 bg-slate-950/40 rounded border border-slate-800/60">
                                                <span className="text-slate-500 font-semibold">6. Fim Real (Deputy):</span>
                                                <span className="text-emerald-400 font-mono font-bold mt-0.5">
                                                    {(() => {
                                                        const s = todayShifts.find(s => (s.plannedStartTime || s.startTime) === checkin.scheduled_start && s.type === 'WORK');
                                                        return s?.endTime ? s.endTime : '--:--';
                                                    })()}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-[9px] text-slate-500 bg-slate-950/50 p-2 rounded-lg border border-slate-800 italic leading-relaxed text-center">
                                            ℹ️ <strong>Dica WhatsApp:</strong> Lembra que os horários do Telegram e do Driver App podem ser checados na mensagem de Login/Token recebida no seu WhatsApp.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
